import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import ReactMarkdown from "react-markdown";
import { Loader2 } from "lucide-react";

import {
  useDeletePromptLibraryItemsMutation,
  useGetPromptLibraryItemsQuery,
  useTogglePromptLibraryItemFavoriteMutation,
} from "../promptLibrary.api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PromptLibraryItem, PromptLibraryTab } from "@/modules/promptLibrary/promptLibrary.types";
import { Button } from "@/components/ui/button";
import { ModifyNewPromptLibraryItemDialog } from "@/modules/promptLibrary/components/modifyNewPromptLibraryItemDialog";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { ApplyPromptLibraryItemDialog } from "@/modules/promptLibrary/components/applyPromptLibraryItemDialog";
import { usePromptLibraryContext } from "@/modules/promptLibrary/promptLibrary.context";
import { customRenderers } from "@/components/feature/markdown/customRenderers";
import { toDefaultDateFormat } from "@/helpers/dates";
import { useGetUsersQuery } from "@/api/users-admin";
import { ConfirmDeletePromptLibraryDialog } from "@/modules/promptLibrary/components/confirmDeletePromptLibraryItemDialog";
import { createGetItemsPayload } from "@/modules/promptLibrary/promptLibrary.utils";
import { PromptLibraryTableActions } from "@/modules/promptLibrary/components/promptLibraryTableActions";

const promptLibraryItemSourceLabelMap = {
  qa: "Q&A",
  open_chat: "Open chat",
};

interface Props {
  activeTab: PromptLibraryTab;
  searchPhrase: string;
}

export const PromptLibraryTable: FC<Props> = ({ activeTab, searchPhrase }) => {
  const { id: userId } = useAuthenticatedUser();

  const getItemsPayload = createGetItemsPayload(searchPhrase, activeTab);

  const { data, isLoading } = useGetPromptLibraryItemsQuery(getItemsPayload);
  const [deletePromptLibraryItems] = useDeletePromptLibraryItemsMutation();
  const [togglePromptLibraryItemFavoriteMutation] = useTogglePromptLibraryItemFavoriteMutation();
  const { setPromptToApply } = usePromptLibraryContext();
  const navigate = useNavigate();
  const userIds = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.map(item => item.user_id);
  }, [data]);
  const userQuery = useGetUsersQuery(userIds);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPromptItem, setEditingPromptItem] = useState<PromptLibraryItem | null>(null);
  const [promptItemToDelete, setPromptItemToDelete] = useState<PromptLibraryItem | null>(null);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);

  const handleFavoriteClick = async ({ id, user_id }: PromptLibraryItem) => {
    if (user_id !== userId) {
      return;
    }
    await togglePromptLibraryItemFavoriteMutation(id);
  };

  const handleEditClick = async (item: PromptLibraryItem) => {
    if (item.user_id !== userId) {
      return;
    }

    setEditingPromptItem(item);
  };

  const handleApplyClick = (item: PromptLibraryItem) => {
    setPromptToApply({
      source: item.source,
      prompt: item.prompt,
    });

    if (item.source === "qa") {
      setIsApplyModalOpen(true);
    } else {
      navigate("/open-chat");
    }
  };

  const handleTrashClick = async (item: PromptLibraryItem) => {
    if (item.user_id !== userId) {
      return;
    }
    setPromptItemToDelete(item);
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between">
        <h1 className="mb-5 text-2xl font-semibold">Prompt Library</h1>
        <Button
          onClick={() => {
            setIsAddModalOpen(true);
          }}
        >
          Add new prompt
        </Button>
      </div>
      <Table className="main-w-full divide-y divide-gray-300">
        <TableHeader>
          <TableRow>
            <TableHead className="divide-y divide-gray-300 bg-gray-50/80">Name</TableHead>
            <TableHead className="max-w-[50%] divide-y divide-gray-300 bg-gray-50/80">Prompt</TableHead>
            <TableHead className="min-w-[10rem] max-w-[50%] divide-y divide-gray-300 bg-gray-50/80">Notes</TableHead>
            <TableHead className="w-[100px] divide-y divide-gray-300 bg-gray-50/80">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="divide-y divide-gray-200 bg-white">
          {data!.length > 0 ? (
            data!.map(item => {
              const user = userQuery.data?.find(({ id }) => id === item.user_id);

              return (
                <TableRow key={item.id}>
                  <TableCell className="align-baseline">
                    <div>
                      <span className="text-gray-600">Name: </span>
                      <span>{item.name}</span>
                    </div>
                    <div className="text-s">
                      <span className="text-gray-600">Author: </span>
                      <span>
                        {user?.name} {user?.surname}
                      </span>
                    </div>

                    <div className="text-s">
                      <span className="text-gray-600">Source: </span>
                      <span>{promptLibraryItemSourceLabelMap[item.source]}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-600">Created: </span>
                      <span className="text-xs">{toDefaultDateFormat(item.created_at)}</span>
                    </div>
                    {item.wasEdited && (
                      <div>
                        <span className="text-xs text-gray-600">Updated: </span>
                        <span className="text-xs">{toDefaultDateFormat(item.updated_at)}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <ReactMarkdown components={customRenderers}>{item.prompt}</ReactMarkdown>
                  </TableCell>
                  <TableCell className="align-top">{item.notes}</TableCell>
                  <TableCell className="align-top">
                    <PromptLibraryTableActions
                      item={item}
                      userId={userId}
                      handleApplyClick={handleApplyClick}
                      handleEditClick={handleEditClick}
                      handleFavoriteClick={handleFavoriteClick}
                      handleTrashClick={handleTrashClick}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                No prompts was found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {isAddModalOpen && (
        <ModifyNewPromptLibraryItemDialog
          isOpen={isAddModalOpen}
          setIsOpen={value => setIsAddModalOpen(value)}
          mode="add"
        />
      )}
      {editingPromptItem && (
        <ModifyNewPromptLibraryItemDialog
          isOpen={!!editingPromptItem}
          setIsOpen={() => setEditingPromptItem(null)}
          item={editingPromptItem}
          mode="edit"
        />
      )}
      {isApplyModalOpen && (
        <ApplyPromptLibraryItemDialog
          isOpen={isApplyModalOpen}
          setIsOpen={value => {
            setPromptToApply(null);
            setIsApplyModalOpen(value);
          }}
        />
      )}
      {promptItemToDelete && (
        <ConfirmDeletePromptLibraryDialog
          isOpen={!!promptItemToDelete}
          setIsOpen={() => setPromptItemToDelete(null)}
          name={promptItemToDelete.name}
          confirmCb={() => {
            deletePromptLibraryItems(promptItemToDelete.id);
            setPromptItemToDelete(null);
          }}
        ></ConfirmDeletePromptLibraryDialog>
      )}
    </>
  );
};
